// @flow
import React, {Fragment} from 'react';
import classNames from 'classnames';
import {filter as filterGraphQlAnywhere} from 'graphql-anywhere';
import Price, {fragments as priceFragments} from '../Price/Price';
import Rating, {fragments as ratingFragments} from '../Rating/Rating';
import Details from '../Details/Details';
import GeoDistance from '../GeoDistance/GeoDistance';
import HitLabel from '../HitLabel/HitLabel';
import BadgeTooltip from './ReviewBadgeTooltip';
import PriceSummary from '@homeaway/react-price-summary';

import type {Props as HitInfoProps} from './HitInfo';
import type {Badge} from './HitInfo';

type Props = {
    premierPartnerBadge: ?Badge,
    rankedBadges: Badge[],
    superlativesBadges: Badge[]
} & HitInfoProps;

const Superlative = ({superlativesBadges}) => {
    if (!superlativesBadges[0].helpText) {
        return (
            <span className="HitInfo__superlative">
                {superlativesBadges[0].name}
            </span>
        );
    }

    return (
        <span>
            <span className="HitInfo__superlative">
                {superlativesBadges[0].name}
            </span>
            <BadgeTooltip helpText={superlativesBadges[0].helpText} />
        </span>
    );
};

// TODO: move minStay logic to lingua
// https://jira.homeawaycorp.com/browse/SERP-2848
export default function HitInfoMobile({
    brand,
    rankedBadges,
    intl,
    isDatelessSearch,
    isMetric,
    listing,
    premierPartnerBadge,
    fullLocale,
    listingHasSecondaryPrice,
    superlativesBadges
}: Props) {
    const {instantBookable, reviewCount, geoDistance} = listing;

    // prevents undesired content shift if there is no reviewBadge
    let priceDetailsClass =
        superlativesBadges &&
        superlativesBadges[0] &&
        superlativesBadges[0].helpText
            ? 'HitInfo__priceSuperlativeGroup--extended'
            : 'HitInfo__priceSuperlativeGroup';

    return (
        <Fragment>
            <div className="media-flex__body">
                <div className={priceDetailsClass}>
                    <div
                        className={classNames('HitInfo__price', {
                            'HitInfo__Price--condensed': listingHasSecondaryPrice
                        })}
                    >
                        {listingHasSecondaryPrice &&
                            !!superlativesBadges &&
                            !!superlativesBadges.length && (
                                <Superlative
                                    superlativesBadges={superlativesBadges}
                                />
                            )}
                        <Price
                            listing={filterGraphQlAnywhere(
                                priceFragments.listing,
                                listing
                            )}
                            useAverageLabel={isDatelessSearch}
                            fullLocale={fullLocale}
                            instantBookable={instantBookable}
                            intl={intl}
                        />
                        {listingHasSecondaryPrice &&
                            listing.priceSummarySecondary && (
                                <span className="PriceSummary__secondary">
                                    <PriceSummary
                                        priceSummary={
                                            listing.priceSummarySecondary
                                        }
                                    />
                                </span>
                            )}
                    </div>
                    {!listingHasSecondaryPrice &&
                        !!superlativesBadges &&
                        !!superlativesBadges.length && (
                            <Superlative
                                superlativesBadges={superlativesBadges}
                            />
                        )}
                </div>
                <div className="HitInfo__details">
                    <Details
                        listing={listing}
                        isMetric={isMetric}
                        locale={intl.locale}
                    />
                </div>
                {geoDistance && (
                    <GeoDistance geoDistance={geoDistance} brand={brand} />
                )}
                <div className="HitInfo__badgeRatingGroup">
                    {reviewCount > 0 && (
                        <div className="HitInfo__rating">
                            <Rating
                                listing={filterGraphQlAnywhere(
                                    ratingFragments.listing,
                                    listing
                                )}
                            />
                        </div>
                    )}
                    {premierPartnerBadge && (
                        <div
                            className="small"
                            data-wdio="premier-partner-badge"
                            aria-hidden="true"
                        >
                            {premierPartnerBadge.name}
                        </div>
                    )}
                </div>
                {rankedBadges.length > 0 && (
                    <div className="HitInfo__labels">
                        {rankedBadges.map((badge, index) => (
                            <HitLabel
                                key={index}
                                id={badge.id}
                                label={badge.name}
                            />
                        ))}
                    </div>
                )}
            </div>
        </Fragment>
    );
}
